import { FC, useEffect, useRef } from 'react';

import { CoreResources } from '../../config/CoreResources';
import { useCoreDispatch } from '../../store/CoreStore';
import { initializationActions } from '../../store/slices/initialization/slice';

import type { BrowserCacheControllerProps, BrowserCacheResource, ResourcePriorityType } from './types';

const createPreloadLinkElement = (resource: BrowserCacheResource<ResourcePriorityType>): HTMLLinkElement => {
  const link = document.createElement('link');

  link.as = resource.kind;
  link.crossOrigin = '';
  link.href = resource.url;
  link.rel = 'preload';

  return link;
};

const schedulePreload = (resources: Record<number, BrowserCacheResource<number>[]>, priority: number): void => {
  if (priority < 6) {
    const resource = resources[priority];
    if (resource) {
      resource.forEach((resource) => {
        const link = createPreloadLinkElement(resource);

        document.head.appendChild(link);
      });
    }

    setTimeout(() => {
      schedulePreload(resources, ++priority);
    }, 1000);
  }
};

const awaitBlockingResources = (resources: BrowserCacheResource<'block'>[]): Promise<void[]> => {
  const promises: Promise<void>[] = resources.map((resource) => {
    return new Promise((resolve, reject) => {
      const link = createPreloadLinkElement(resource);

      document.head.appendChild(link);

      link.onload = (): void => resolve();
      link.onerror = (): void => reject();
    });
  });

  return Promise.all(promises);
};

export const BrowserCacheControllerConnector: FC<BrowserCacheControllerProps> = ({ resources }) => {
  const dispatch = useCoreDispatch();
  const isInitialisedRef = useRef(false);

  useEffect(() => {
    if (isInitialisedRef.current) return;
    isInitialisedRef.current = true;

    const combinedResources = { ...resources, ...CoreResources };

    const resourcesByPriority: Record<number, BrowserCacheResource<number>[]> = {};
    const blockingResources: BrowserCacheResource<'block'>[] = [];

    for (const key in combinedResources) {
      const resource = combinedResources[key];
      if (resource.priority === 'block') {
        blockingResources.push(resource as BrowserCacheResource<'block'>);
        continue;
      }
      const bucket = resourcesByPriority[resource.priority] || [];
      bucket.push(resource as BrowserCacheResource<number>);
      resourcesByPriority[resource.priority] = bucket;
    }

    awaitBlockingResources(blockingResources).then(() => {
      dispatch(initializationActions.setBlockingResourcesReady(true));
    });

    setTimeout(() => {
      schedulePreload(resourcesByPriority, 1);
    }, 10_000);
  }, [resources, dispatch]);
  return null;
};
